import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { FieldErrors, FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Category } from '@/interfaces/catalog/Category'
import API from '@/lib/api'
import { components } from '@invo.space/shared'

import { CategoryForm } from './CategoryForm'
import { CategoryFormSchema, CategoryFormValues } from './schema'

interface CategoryFormModalProps {
  isOpen: boolean
  onClose: () => void
  category?: Category
  defaultParentId?: string | null
  categories?: Category[]
  onSuccess?: () => void
}

export const CategoryFormModal = ({
  isOpen,
  onClose,
  category,
  defaultParentId = null,
  categories = [],
  onSuccess,
}: CategoryFormModalProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'catalog.categories',
  })

  const methods = useForm<CategoryFormValues>({
    resolver: zodResolver(CategoryFormSchema),
    defaultValues: {
      name: '',
      parentId: null,
      // externalId: null,
      position: null,
    },
  })

  useEffect(() => {
    if (!isOpen) {
      return
    }

    if (category?.id) {
      methods.reset({
        name: category.name,
        parentId: category.parentId,
        position: category.position,
      })
    } else {
      methods.reset({
        name: '',
        parentId: defaultParentId,
        position: null,
      })
    }
  }, [category, defaultParentId, isOpen, methods])

  const onValid = async (values: CategoryFormValues) => {
    try {
      if (category?.id) {
        await API.patch<components['schemas']['Category']>(
          `/api/v2/categories/${category.id}`,
          {
            name: values.name,
            parentId: values.parentId,
            // externalId: values.externalId,
            position: values.position,
          },
        )

        toast.success(t('edit.success'))
      } else {
        await API.post<components['schemas']['Category']>(
          '/api/v2/categories',
          {
            name: values.name,
            parentId: values.parentId,
            // externalId: values.externalId,
            position: values.position,
          },
        )

        toast.success(t('new.success'))
      }

      onSuccess?.()
      onClose()
      methods.reset()
    } catch (error: unknown) {
      console.error('Category form error:', error)
      const errorMessage =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data
              ?.message
          : undefined
      toast.error(category?.id ? t('edit.error') : t('new.error'), {
        description: errorMessage || t('error.description'),
      })
    }
  }

  const onInvalid = (errors: FieldErrors<CategoryFormValues>) => {
    console.log('Form errors', errors, methods.getValues())
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose()
      methods.reset()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {category?.id ? t('edit.title') : t('new.title')}
          </DialogTitle>
        </DialogHeader>

        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onValid, onInvalid)}
            className="w-full space-y-6"
          >
            <CategoryForm
              categories={categories}
              editingCategoryId={category?.id}
            />

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                {t('form.cancel')}
              </Button>
              <Button type="submit">
                {t('form.save')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}

